/**
 * Edit Invoice Page
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState } from '../components/ui';
import { InvoiceForm } from '../components/invoices/InvoiceForm';
import {
  useInvoice,
  useUpdateInvoice,
  useAddInvoiceItem,
  useUpdateInvoiceItem,
  useDeleteInvoiceItem,
} from '../hooks/useInvoices';
import { useToastHelpers } from '../components/ui/Toast';
import type { CreateInvoiceInput, CreateInvoiceItemInput } from '@armard/shared';

export const Route = createFileRoute('/invoices_/$id_/edit')({
  component: EditInvoicePage,
});

function EditInvoicePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: invoice, isLoading, error } = useInvoice(id);
  const updateInvoice = useUpdateInvoice();
  const createItem = useAddInvoiceItem(id);
  const updateItem = useUpdateInvoiceItem(id);
  const deleteItem = useDeleteInvoiceItem(id);

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !invoice) {
    return (
      <ProtectedRoute>
        <AppShell>
          <EmptyState
            title="Rechnung nicht gefunden"
            description="Die angeforderte Rechnung existiert nicht oder wurde gelöscht."
            action={{
              label: 'Zurück zu Rechnungen',
              onClick: () => navigate({ to: '/invoices' }),
            }}
          />
        </AppShell>
      </ProtectedRoute>
    );
  }

  const handleSubmit = async (invoiceData: CreateInvoiceInput, items: CreateInvoiceItemInput[]) => {
    try {
      // Update invoice metadata
      await updateInvoice.mutateAsync({
        id: invoice.id,
        ...invoiceData,
        version: invoice.version,
      });

      // Delete removed items
      for (const existing of invoice.items) {
        // Simple heuristic: if no item in the new list matches this one, delete it
        const stillExists = items.some(
          (i) => i.description === existing.description
        );
        if (!stillExists) {
          await deleteItem.mutateAsync(existing.id);
        }
      }

      // Create or update items
      for (const itemData of items) {
        const existing = invoice.items.find(
          (i) => i.description === itemData.description
        );
        if (existing) {
          // Update existing item
          await updateItem.mutateAsync({
            itemId: existing.id,
            ...itemData,
            discountType: itemData.discountType ?? null,
            discountValue: itemData.discountValue ?? null,
          });
        } else {
          // Create new item
          await createItem.mutateAsync(itemData);
        }
      }

      toast.success('Rechnung aktualisiert');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to="/invoices/$id"
              params={{ id: invoice.id }}
              className="text-white/40 hover:text-white/60"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-white/40">{invoice.invoiceNumber}</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">Rechnung bearbeiten</h1>
        </div>

        <InvoiceForm
          invoice={invoice}
          onSubmit={handleSubmit}
          isLoading={updateInvoice.isPending}
          onCancel={() => navigate({ to: '/invoices/$id', params: { id: invoice.id } })}
        />
      </AppShell>
    </ProtectedRoute>
  );
}
